import * as React from "react";
import { X, ChevronDown } from "lucide-react";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../select";
import { cn } from "../../lib/utils";

export type SelectV2Option<T = unknown> = {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
  textValue?: string;
  data?: T;
};

export type SelectV2Props<T = unknown> = {
  value: string;
  onChange: (value: string) => void;
  options: SelectV2Option<T>[];
  placeholder?: string;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  optionClassName?: string;
  label?: React.ReactNode;
  ariaLabel?: string;
  disabled?: boolean;
  readonly?: boolean;
  clearable?: boolean;
  onClear?: () => void;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  sideOffset?: number;
};

export type SelectV2TriggerProps = {
  placeholder: string;
  ariaLabel?: string;
  disabled?: boolean;
  className?: string;
};

export const SelectV2Trigger = React.forwardRef<HTMLButtonElement, SelectV2TriggerProps>(
  ({ placeholder, ariaLabel, disabled = false, className }, ref) => (
    <SelectTrigger
      ref={ref}
      aria-label={ariaLabel}
      disabled={disabled}
      iconClassName="text-muted-foreground opacity-100"
      className={cn(
        "min-h-10 border-gray-200 bg-white text-foreground dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100",
        "disabled:cursor-not-allowed disabled:bg-[#F3F3F5] disabled:opacity-50 dark:disabled:bg-gray-800",
        className,
      )}
    >
      <SelectValue placeholder={placeholder} />
    </SelectTrigger>
  ),
);

SelectV2Trigger.displayName = "SelectV2Trigger";

export const SelectV2Content = React.forwardRef<
  React.ElementRef<typeof SelectContent>,
  React.ComponentPropsWithoutRef<typeof SelectContent>
>(({ className, ...props }, ref) => (
  <SelectContent
    ref={ref}
    className={cn(
      "z-[1401] max-h-[250px] border-gray-200 bg-popover text-popover-foreground dark:border-gray-700 dark:bg-gray-800",
      className,
    )}
    {...props}
  />
));

SelectV2Content.displayName = "SelectV2Content";

export type SelectV2OptionItemProps<T = unknown> = {
  option: SelectV2Option<T>;
  className?: string;
};

export function SelectV2OptionItem<T = unknown>({ option, className }: SelectV2OptionItemProps<T>) {
  const textValue =
    option.textValue ?? (typeof option.label === "string" ? option.label : option.value);

  return (
    <SelectItem
      value={option.value}
      disabled={option.disabled}
      textValue={textValue}
      className={cn(
        "min-h-8 text-sm text-foreground dark:text-gray-100",
        "data-[state=checked]:bg-emerald-50 dark:data-[state=checked]:bg-emerald-950/50",
        className,
      )}
    >
      {option.label}
    </SelectItem>
  );
}

export function SelectV2<T = unknown>({
  value,
  onChange,
  options,
  placeholder = "Select...",
  className,
  triggerClassName,
  contentClassName,
  optionClassName,
  label,
  ariaLabel,
  disabled = false,
  readonly = false,
  clearable = false,
  onClear,
  side = "bottom",
  align = "start",
  sideOffset = 8,
}: SelectV2Props<T>) {
  const selectedOption = React.useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );
  const hasValue = Boolean(selectedOption);
  const isInteractive = !disabled && !readonly;

  const clearValue = () => {
    if (onClear) {
      onClear();
      return;
    }
    onChange("");
  };

  if (readonly) {
    return (
      <div className={cn("flex w-full flex-col gap-1.5", className)}>
        {label ? <div className="text-sm font-medium text-foreground">{label}</div> : null}
        <div
          aria-label={ariaLabel ?? (typeof label === "string" ? label : undefined)}
          aria-readonly="true"
          className={cn(
            "flex h-10 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm shadow-none",
            "text-foreground dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100",
            triggerClassName,
          )}
        >
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-sm md:text-sm",
              hasValue
                ? "text-foreground dark:text-gray-100"
                : "text-muted-foreground dark:text-gray-400",
            )}
          >
            {selectedOption?.label ?? placeholder}
          </span>
          <ChevronDown
            className="h-4 w-4 shrink-0 text-muted-foreground"
            size={16}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex w-full flex-col gap-1.5", className)}>
      {label ? <div className="text-sm font-medium text-foreground">{label}</div> : null}
      <div className="relative flex items-center gap-2">
        <Select
          value={value}
          onValueChange={onChange}
          disabled={disabled}
        >
          <SelectV2Trigger
            placeholder={placeholder}
            ariaLabel={ariaLabel ?? (typeof label === "string" ? label : undefined)}
            className={triggerClassName}
          />
          <SelectV2Content
            side={side}
            align={align}
            sideOffset={sideOffset}
            className={contentClassName}
          >
            {options.map((option) => (
              <SelectV2OptionItem
                key={option.value}
                option={option}
                className={optionClassName}
              />
            ))}
          </SelectV2Content>
        </Select>

        {clearable && hasValue && isInteractive ? (
          <button
            type="button"
            aria-label="Clear selection"
            title="Clear selection"
            className="absolute right-8 top-1/2 z-10 -translate-y-1/2 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-100"
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => {
              event.stopPropagation();
              clearValue();
            }}
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
}
